import consultantsData from '../data/consultants.json';
import rotationsData from '../data/rotations.json';
import weekendRotaData from '../data/weekendRota.json';

class DataService {
  constructor() {
    this.consultants = consultantsData.consultants;
    this.leaveReasons = consultantsData.leaveReasons;
    this.rotations = rotationsData;
    this.weekendRota = weekendRotaData.fridayWeekendRota;
    this.apiBase = '/.netlify/functions';
    // Check if running on Netlify - detect by checking if functions are available
    // In production, always try to use Netlify Functions first, fallback to localStorage if they fail
    this.isNetlify = window.location.hostname !== 'localhost' &&
                     window.location.hostname !== '127.0.0.1' &&
                     !window.location.hostname.startsWith('192.168.');
  }

  // Static data methods
  getConsultants() {
    return this.consultants;
  }

  getConsultantByInitials(initials) {
    return this.consultants.find(c => c.initials === initials);
  }

  getConsultantByName(name) {
    return this.consultants.find(c => c.name === name);
  }

  getLeaveReasons() {
    return this.leaveReasons;
  }

  getRotations() {
    return this.rotations;
  }

  getWeekendRota() {
    return this.weekendRota;
  }

  // Dynamic data methods (using Netlify Functions or localStorage)
  async getLeave() {
    if (!this.isNetlify) {
      // Use localStorage for local development
      const stored = localStorage.getItem('trauma-planner-leave');
      return stored ? JSON.parse(stored) : [];
    }

    try {
      const response = await fetch(`${this.apiBase}/leave-get`);
      if (!response.ok) {
        // Fallback to localStorage if Netlify Functions not available
        console.log('Netlify Functions not available, using localStorage');
        const stored = localStorage.getItem('trauma-planner-leave');
        return stored ? JSON.parse(stored) : [];
      }
      const data = await response.json();
      return data.leave || [];
    } catch (error) {
      console.error('Error fetching leave, falling back to localStorage:', error);
      const stored = localStorage.getItem('trauma-planner-leave');
      return stored ? JSON.parse(stored) : [];
    }
  }

  async addLeave(leaveEntry) {
    if (!this.isNetlify) {
      // Use localStorage for local development
      const existing = await this.getLeave();
      const newEntry = {
        ...leaveEntry,
        id: leaveEntry.id || Date.now() + Math.random()
      };
      const updated = [...existing, newEntry];
      localStorage.setItem('trauma-planner-leave', JSON.stringify(updated));
      return { entry: newEntry };
    }

    try {
      const response = await fetch(`${this.apiBase}/leave-add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(leaveEntry)
      });
      if (!response.ok) {
        // Fallback to localStorage if Netlify Functions not available
        console.log('Netlify Functions not available, using localStorage');
        const existing = await this.getLeave();
        const newEntry = {
          ...leaveEntry,
          id: leaveEntry.id || Date.now() + Math.random()
        };
        const updated = [...existing, newEntry];
        localStorage.setItem('trauma-planner-leave', JSON.stringify(updated));
        return { entry: newEntry };
      }
      return await response.json();
    } catch (error) {
      console.error('Error adding leave, falling back to localStorage:', error);
      // Fallback to localStorage
      const existing = await this.getLeave();
      const newEntry = {
        ...leaveEntry,
        id: leaveEntry.id || Date.now() + Math.random()
      };
      const updated = [...existing, newEntry];
      localStorage.setItem('trauma-planner-leave', JSON.stringify(updated));
      return { entry: newEntry };
    }
  }

  async removeLeave(leaveId) {
    if (!this.isNetlify) {
      // Use localStorage for local development
      const existing = await this.getLeave();
      const updated = existing.filter(entry => entry.id !== leaveId);
      localStorage.setItem('trauma-planner-leave', JSON.stringify(updated));
      return { success: true };
    }

    try {
      const response = await fetch(`${this.apiBase}/leave-remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: leaveId })
      });
      if (!response.ok) throw new Error('Failed to remove leave');
      return await response.json();
    } catch (error) {
      console.error('Error removing leave:', error);
      throw error;
    }
  }

  async getSwaps() {
    if (!this.isNetlify) {
      // Use localStorage for local development
      const stored = localStorage.getItem('trauma-planner-swaps');
      return stored ? JSON.parse(stored) : {};
    }

    try {
      const response = await fetch(`${this.apiBase}/swaps-get`);
      if (!response.ok) throw new Error('Failed to fetch swaps');
      const data = await response.json();
      return data.swaps || {};
    } catch (error) {
      console.error('Error fetching swaps:', error);
      return {};
    }
  }

  async addSwap(weekDay, swap) {
    if (!this.isNetlify) {
      // Use localStorage for local development
      const existing = await this.getSwaps();
      existing[weekDay] = swap;
      localStorage.setItem('trauma-planner-swaps', JSON.stringify(existing));
      return { success: true };
    }

    try {
      const response = await fetch(`${this.apiBase}/swaps-add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ weekDay, swap })
      });
      if (!response.ok) throw new Error('Failed to add swap');
      return await response.json();
    } catch (error) {
      console.error('Error adding swap:', error);
      throw error;
    }
  }

  async addSwapsBatch(swapsObject) {
    if (!this.isNetlify) {
      // Use localStorage for local development
      const existing = await this.getSwaps();
      Object.assign(existing, swapsObject);
      localStorage.setItem('trauma-planner-swaps', JSON.stringify(existing));
      return { success: true };
    }

    try {
      const response = await fetch(`${this.apiBase}/swaps-add-batch`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ swaps: swapsObject })
      });
      if (!response.ok) throw new Error('Failed to add swaps batch');
      return await response.json();
    } catch (error) {
      console.error('Error adding swaps batch:', error);
      throw error;
    }
  }

  async removeSwap(weekDay) {
    if (!this.isNetlify) {
      // Use localStorage for local development
      const existing = await this.getSwaps();
      delete existing[weekDay];
      localStorage.setItem('trauma-planner-swaps', JSON.stringify(existing));
      return { success: true };
    }

    try {
      const response = await fetch(`${this.apiBase}/swaps-remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ weekDay })
      });
      if (!response.ok) throw new Error('Failed to remove swap');
      return await response.json();
    } catch (error) {
      console.error('Error removing swap:', error);
      throw error;
    }
  }

  async getOverrides() {
    if (!this.isNetlify) {
      // Use localStorage for local development
      const stored = localStorage.getItem('trauma-planner-overrides');
      return stored ? JSON.parse(stored) : { packhamClarke: {}, bakerBick: {} };
    }

    try {
      const response = await fetch(`${this.apiBase}/overrides-get`);
      if (!response.ok) throw new Error('Failed to fetch overrides');
      const data = await response.json();
      return data.overrides || { packhamClarke: {}, bakerBick: {} };
    } catch (error) {
      console.error('Error fetching overrides:', error);
      return { packhamClarke: {}, bakerBick: {} };
    }
  }

  async updateOverrides(overrides) {
    if (!this.isNetlify) {
      // Use localStorage for local development
      localStorage.setItem('trauma-planner-overrides', JSON.stringify(overrides));
      return { success: true };
    }

    try {
      const response = await fetch(`${this.apiBase}/overrides-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(overrides)
      });
      if (!response.ok) throw new Error('Failed to update overrides');
      return await response.json();
    } catch (error) {
      console.error('Error updating overrides:', error);
      throw error;
    }
  }

  async getAdditionalActivities() {
    if (!this.isNetlify) {
      // Use localStorage for local development
      const stored = localStorage.getItem('trauma-planner-additional-activities');
      return stored ? JSON.parse(stored) : {};
    }

    try {
      const response = await fetch(`${this.apiBase}/additional-activities-get`);
      if (!response.ok) throw new Error('Failed to fetch additional activities');
      const data = await response.json();
      return data.additionalActivities || {};
    } catch (error) {
      console.error('Error fetching additional activities:', error);
      return {};
    }
  }

  async addAdditionalActivity(date, activityName) {
    if (!this.isNetlify) {
      // Use localStorage for local development
      const existing = await this.getAdditionalActivities();
      if (!existing[date]) {
        existing[date] = [];
      }
      if (!existing[date].includes(activityName)) {
        existing[date].push(activityName);
      }
      localStorage.setItem('trauma-planner-additional-activities', JSON.stringify(existing));
      return { success: true };
    }

    try {
      const response = await fetch(`${this.apiBase}/additional-activities-add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, activityName })
      });
      if (!response.ok) throw new Error('Failed to add additional activity');
      return await response.json();
    } catch (error) {
      console.error('Error adding additional activity:', error);
      throw error;
    }
  }

  async removeAdditionalActivity(date, activityName) {
    if (!this.isNetlify) {
      // Use localStorage for local development
      const existing = await this.getAdditionalActivities();
      if (existing[date]) {
        existing[date] = existing[date].filter(name => name !== activityName);
        if (existing[date].length === 0) {
          delete existing[date];
        }
      }
      localStorage.setItem('trauma-planner-additional-activities', JSON.stringify(existing));
      return { success: true };
    }

    try {
      const response = await fetch(`${this.apiBase}/additional-activities-remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, activityName })
      });
      if (!response.ok) throw new Error('Failed to remove additional activity');
      return await response.json();
    } catch (error) {
      console.error('Error removing additional activity:', error);
      throw error;
    }
  }

  async getAdditionalPeople() {
    if (!this.isNetlify) {
      // Use localStorage for local development
      const stored = localStorage.getItem('trauma-planner-additional-people');
      return stored ? JSON.parse(stored) : {};
    }

    try {
      const response = await fetch(`${this.apiBase}/additional-people-get`);
      if (!response.ok) throw new Error('Failed to fetch additional people');
      const data = await response.json();
      return data.additionalPeople || {};
    } catch (error) {
      console.error('Error fetching additional people:', error);
      return {};
    }
  }

  async addAdditionalPerson(date, name) {
    if (!this.isNetlify) {
      // Use localStorage for local development
      const existing = await this.getAdditionalPeople();
      if (!existing[date]) {
        existing[date] = [];
      }
      if (!existing[date].includes(name)) {
        existing[date].push(name);
      }
      localStorage.setItem('trauma-planner-additional-people', JSON.stringify(existing));
      return { success: true };
    }

    try {
      const response = await fetch(`${this.apiBase}/additional-people-add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, name })
      });
      if (!response.ok) throw new Error('Failed to add additional person');
      return await response.json();
    } catch (error) {
      console.error('Error adding additional person:', error);
      throw error;
    }
  }

  async removeAdditionalPerson(date, name) {
    if (!this.isNetlify) {
      // Use localStorage for local development
      const existing = await this.getAdditionalPeople();
      if (existing[date]) {
        existing[date] = existing[date].filter(n => n !== name);
        if (existing[date].length === 0) {
          delete existing[date];
        }
      }
      localStorage.setItem('trauma-planner-additional-people', JSON.stringify(existing));
      return { success: true };
    }

    try {
      const response = await fetch(`${this.apiBase}/additional-people-remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, name })
      });
      if (!response.ok) throw new Error('Failed to remove additional person');
      return await response.json();
    } catch (error) {
      console.error('Error removing additional person:', error);
      throw error;
    }
  }

  async getWeekendRotaOverrides() {
    if (!this.isNetlify) {
      // Use localStorage for local development
      const stored = localStorage.getItem('trauma-planner-weekend-rota-overrides');
      return stored ? JSON.parse(stored) : {};
    }

    try {
      const response = await fetch(`${this.apiBase}/weekend-rota-overrides-get`);
      if (!response.ok) throw new Error('Failed to fetch weekend rota overrides');
      const data = await response.json();
      return data.overrides || {};
    } catch (error) {
      console.error('Error fetching weekend rota overrides:', error);
      return {};
    }
  }

  async addWeekendRotaOverride(date, consultantInitials) {
    if (!this.isNetlify) {
      // Use localStorage for local development
      const existing = await this.getWeekendRotaOverrides();
      existing[date] = consultantInitials;
      localStorage.setItem('trauma-planner-weekend-rota-overrides', JSON.stringify(existing));
      return { success: true };
    }

    try {
      const response = await fetch(`${this.apiBase}/weekend-rota-overrides-add`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date, consultantInitials })
      });
      if (!response.ok) throw new Error('Failed to add weekend rota override');
      return await response.json();
    } catch (error) {
      console.error('Error adding weekend rota override:', error);
      throw error;
    }
  }

  async removeWeekendRotaOverride(date) {
    if (!this.isNetlify) {
      // Use localStorage for local development
      const existing = await this.getWeekendRotaOverrides();
      delete existing[date];
      localStorage.setItem('trauma-planner-weekend-rota-overrides', JSON.stringify(existing));
      return { success: true };
    }

    try {
      const response = await fetch(`${this.apiBase}/weekend-rota-overrides-remove`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date })
      });
      if (!response.ok) throw new Error('Failed to remove weekend rota override');
      return await response.json();
    } catch (error) {
      console.error('Error removing weekend rota override:', error);
      throw error;
    }
  }
}

export default new DataService();